import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { 
  Users, Check, X, Eye, Clock, CheckCircle, XCircle, AlertCircle,
  Search, Filter, RefreshCw, Mail, Phone, CreditCard, Bike, Car, Building, FileText, ExternalLink
} from 'lucide-react';

/**
 * ========================================
 * DRIVER APPLICATIONS ADMIN
 * ========================================
 * Manage driver applications - view, approve, reject
 */

const DriverApplicationsAdmin = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // 'all', 'pending', 'approved', 'rejected'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [approvalDetails, setApprovalDetails] = useState(null); // Store approved driver details for WhatsApp

  // Fetch applications
  const fetchApplications = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('driver_applications')
        .select('*')
        .order('created_at', { ascending: false });

      if (filter !== 'all') {
        query = query.eq('status', filter);
      }

      const { data, error } = await query;

      if (error) throw error;
      setApplications(data || []);
    } catch (err) {
      console.error('Error fetching applications:', err);
      setError('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, [filter]);

  // Filter applications by search
  const filteredApplications = applications.filter(app => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      app.full_name.toLowerCase().includes(query) ||
      app.email.toLowerCase().includes(query) ||
      app.phone.includes(query) ||
      app.id_number.includes(query) ||
      (app.vehicle_registration || app.bike_registration || '').toLowerCase().includes(query)
    );
  });

  // View application details
  const handleView = (application) => {
    setSelectedApplication(application);
    setRejectionReason('');
    setShowModal(true);
  };

  // Approve application
  const handleApprove = async () => {
    if (!selectedApplication) return;

    setProcessing(true);
    setError('');

    try {
      // 1. Create Supabase auth user
      const tempPassword = generateTempPassword();
      
      const { data: authData, error: authError } = await supabase.auth.admin.createUser({
        email: selectedApplication.email,
        password: tempPassword,
        email_confirm: true,
        user_metadata: {
          full_name: selectedApplication.full_name,
          role: 'driver'
        }
      });

      // If admin API not available, use regular signup
      let userId;
      if (authError) {
        // Try regular signup instead
        const { data: signupData, error: signupError } = await supabase.auth.signUp({
          email: selectedApplication.email,
          password: tempPassword,
          options: {
            data: {
              full_name: selectedApplication.full_name,
              role: 'driver'
            }
          }
        });

        if (signupError) throw signupError;
        userId = signupData.user?.id;
      } else {
        userId = authData.user?.id;
      }

      if (!userId) {
        throw new Error('Failed to create user account');
      }

      // 2. Update user_profiles with correct name and role
      const { error: profileError } = await supabase
        .from('user_profiles')
        .upsert({
          id: userId,
          name: selectedApplication.full_name,
          email: selectedApplication.email,
          phone: selectedApplication.phone,
          role: 'driver'
        }, { onConflict: 'id' });

      if (profileError) {
        console.error('Error updating user profile:', profileError);
        // Don't fail - continue with driver creation
      }

      // 3. Create driver record with must_change_password flag
      const { error: driverError } = await supabase
        .from('drivers')
        .insert({
          user_id: userId,
          name: selectedApplication.full_name,
          phone: selectedApplication.phone,
          id_number: selectedApplication.id_number,
          id_type: selectedApplication.id_type,
          vehicle_type: selectedApplication.vehicle_type || 'bike',
          vehicle_registration: selectedApplication.vehicle_registration || selectedApplication.bike_registration,
          bank_name: selectedApplication.bank_name,
          account_number: selectedApplication.account_number,
          photo_url: selectedApplication.photo_url,
          status: 'active',
          application_id: selectedApplication.id,
          must_change_password: true
        });

      if (driverError) throw driverError;

      // 4. Update application status
      const { error: updateError } = await supabase
        .from('driver_applications')
        .update({
          status: 'approved',
          reviewed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedApplication.id);

      if (updateError) throw updateError;

      // 5. Send password reset email so driver can set their own password
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        selectedApplication.email,
        { redirectTo: 'https://driver.onthemoov.co.za/reset-password' }
      );

      if (resetError) {
        console.error('Password reset email error:', resetError);
        // Don't fail the whole process, just warn
      }

      // 6. Prepare WhatsApp message and AUTO-OPEN IT
      const whatsappPhone = selectedApplication.phone.replace(/^0/, '27');
      const whatsappMessage = `Hi ${selectedApplication.full_name.split(' ')[0]},

🎉 *Welcome to Foodline Delivery Team!*

Your driver account has been approved. Here are your login details:

📧 *Email:* ${selectedApplication.email}
🔑 *Temporary Password:* ${tempPassword}

📱 *Driver App:* https://driver.onthemoov.co.za

⚠️ *IMPORTANT:* You will be required to change your password when you first log in.

Good luck! 🚀`;

      const whatsappUrl = `https://wa.me/${whatsappPhone}?text=${encodeURIComponent(whatsappMessage)}`;

      // Store details for the confirmation modal
      setApprovalDetails({
        name: selectedApplication.full_name,
        email: selectedApplication.email,
        phone: selectedApplication.phone,
        password: tempPassword,
        whatsappUrl: whatsappUrl
      });
      
      // ✅ AUTO-OPEN WhatsApp immediately!
      window.open(whatsappUrl, '_blank');
      
      setSuccess('Driver approved! WhatsApp opened - please send the message.');
      setShowModal(false);
      fetchApplications();
    } catch (err) {
      console.error('Approval error:', err);
      setError(err.message || 'Failed to approve application');
    } finally {
      setProcessing(false);
    }
  };

  // Reject application
  const handleReject = async () => {
    if (!selectedApplication) return;

    if (!rejectionReason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    setProcessing(true);
    setError('');

    try {
      const { error: updateError } = await supabase
        .from('driver_applications')
        .update({
          status: 'rejected',
          rejection_reason: rejectionReason.trim(),
          reviewed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedApplication.id);

      if (updateError) throw updateError;

      setSuccess('Application rejected');
      setShowModal(false);
      fetchApplications();
    } catch (err) {
      console.error('Rejection error:', err);
      setError('Failed to reject application');
    } finally {
      setProcessing(false);
    }
  };

  // Send password reset email
  const handleSendPasswordReset = async (email) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: 'https://driver.onthemoov.co.za/reset-password'
      });
      
      if (error) throw error;
      
      setSuccess(`Password reset email sent to ${email}`);
    } catch (err) {
      console.error('Password reset error:', err);
      setError('Failed to send password reset email: ' + err.message);
    }
  };

  // Generate temporary password
  const generateTempPassword = () => {
    // Default password for all new drivers - they can change it to anything they want
    return '123456';
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Status badge
  const StatusBadge = ({ status }) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    const icons = {
      pending: <Clock size={14} />,
      approved: <CheckCircle size={14} />,
      rejected: <XCircle size={14} />
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Stats
  const stats = {
    total: applications.length,
    pending: applications.filter(a => a.status === 'pending').length,
    approved: applications.filter(a => a.status === 'approved').length,
    rejected: applications.filter(a => a.status === 'rejected').length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Driver Applications</h1>
          <p className="text-gray-600 mt-1">Review and manage driver registration applications</p>
        </div>
        <button
          onClick={fetchApplications}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <RefreshCw size={18} />
          Refresh
        </button>
      </div>

      {/* Success/Error Messages */}
      {success && !approvalDetails && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg whitespace-pre-line">
          {success}
          <button onClick={() => setSuccess('')} className="float-right"><X size={18} /></button>
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
          <button onClick={() => setError('')} className="float-right"><X size={18} /></button>
        </div>
      )}

      {/* WhatsApp Notification Modal - Shows credentials after approval */}
      {approvalDetails && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Driver Approved!</h2>
              <p className="text-gray-600 mt-1">WhatsApp should have opened. Make sure to send the message!</p>
            </div>

            {/* Credentials Display - For reference */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4 space-y-3">
              <div>
                <div className="text-xs text-gray-500 uppercase">Driver Name</div>
                <div className="font-medium text-gray-900">{approvalDetails.name}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase">Email (Login)</div>
                <div className="font-medium text-gray-900">{approvalDetails.email}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase">Temporary Password</div>
                <div className="font-mono text-lg font-bold text-blue-600 bg-blue-50 px-3 py-2 rounded">{approvalDetails.password}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase">Phone</div>
                <div className="font-medium text-gray-900">{approvalDetails.phone}</div>
              </div>
            </div>

            {/* WhatsApp Button - In case popup was blocked */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-yellow-800 mb-2">
                <strong>⚠️ Popup blocked?</strong> Click below to open WhatsApp:
              </p>
              <a
                href={approvalDetails.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-[#25D366] text-white rounded-lg font-bold hover:bg-[#20bd5a] transition-colors"
              >
                <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Open WhatsApp Again
              </a>
            </div>

            {/* Copy Credentials Button */}
            <button
              onClick={() => {
                const text = `Email: ${approvalDetails.email}\nPassword: ${approvalDetails.password}`;
                navigator.clipboard.writeText(text);
                alert('Credentials copied to clipboard!');
              }}
              className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors mb-3"
            >
              📋 Copy Credentials to Clipboard
            </button>

            <button
              onClick={() => setApprovalDetails(null)}
              className="w-full px-4 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          <div className="text-sm text-gray-600">Total Applications</div>
        </div>
        <div className="bg-yellow-50 rounded-xl shadow-sm p-4 border border-yellow-200">
          <div className="text-2xl font-bold text-yellow-700">{stats.pending}</div>
          <div className="text-sm text-yellow-600">Pending Review</div>
        </div>
        <div className="bg-green-50 rounded-xl shadow-sm p-4 border border-green-200">
          <div className="text-2xl font-bold text-green-700">{stats.approved}</div>
          <div className="text-sm text-green-600">Approved</div>
        </div>
        <div className="bg-red-50 rounded-xl shadow-sm p-4 border border-red-200">
          <div className="text-2xl font-bold text-red-700">{stats.rejected}</div>
          <div className="text-sm text-red-600">Rejected</div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Status Filter */}
          <div className="flex gap-2">
            {['all', 'pending', 'approved', 'rejected'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === status
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, phone, ID..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Applications List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : filteredApplications.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <Users size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No applications found</h3>
          <p className="text-gray-600">
            {filter === 'pending' 
              ? 'No pending applications to review.'
              : 'No applications match your search criteria.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Applicant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vehicle
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Applied
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredApplications.map((app) => (
                  <tr key={app.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <img
                          src={app.photo_url}
                          alt={app.full_name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        <div className="ml-3">
                          <div className="font-medium text-gray-900">{app.full_name}</div>
                          <div className="text-sm text-gray-500">{app.id_number}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{app.email}</div>
                      <div className="text-sm text-gray-500">{app.phone}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {app.vehicle_type === 'car' ? (
                          <Car size={16} className="text-blue-500" />
                        ) : (
                          <Bike size={16} className="text-green-500" />
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900">{app.vehicle_registration || app.bike_registration}</div>
                          <div className="text-xs text-gray-500 capitalize">{app.vehicle_type || 'Bike'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={app.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(app.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleView(app)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm font-medium"
                      >
                        <Eye size={16} />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Application Detail Modal */}
      {showModal && selectedApplication && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Application Details</h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">
                  {error}
                </div>
              )}

              {/* Photo & Basic Info */}
              <div className="flex items-start gap-6 mb-6">
                <img
                  src={selectedApplication.photo_url}
                  alt={selectedApplication.full_name}
                  className="w-32 h-32 rounded-xl object-cover border-2 border-gray-200"
                />
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {selectedApplication.full_name}
                  </h3>
                  <StatusBadge status={selectedApplication.status} />
                  <p className="text-sm text-gray-500 mt-2">
                    Applied: {formatDate(selectedApplication.created_at)}
                  </p>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-gray-600 mb-1">
                    <Mail size={16} />
                    <span className="text-sm">Email</span>
                  </div>
                  <div className="font-medium text-gray-900">{selectedApplication.email}</div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-gray-600 mb-1">
                    <Phone size={16} />
                    <span className="text-sm">Phone</span>
                  </div>
                  <div className="font-medium text-gray-900">{selectedApplication.phone}</div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-gray-600 mb-1">
                    <CreditCard size={16} />
                    <span className="text-sm">{selectedApplication.id_type === 'sa_id' ? 'SA ID Number' : 'Passport Number'}</span>
                  </div>
                  <div className="font-medium text-gray-900">{selectedApplication.id_number}</div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-gray-600 mb-1">
                    {selectedApplication.vehicle_type === 'car' ? (
                      <Car size={16} />
                    ) : (
                      <Bike size={16} />
                    )}
                    <span className="text-sm">Vehicle ({selectedApplication.vehicle_type === 'car' ? 'Car' : 'Motorbike'})</span>
                  </div>
                  <div className="font-medium text-gray-900">{selectedApplication.vehicle_registration || selectedApplication.bike_registration}</div>
                </div>
              </div>

              {/* Banking Details */}
              {(selectedApplication.bank_name || selectedApplication.account_number) && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Building size={16} />
                    Banking Details
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedApplication.bank_name && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="text-sm text-gray-600 mb-1">Bank</div>
                        <div className="font-medium text-gray-900">{selectedApplication.bank_name}</div>
                      </div>
                    )}
                    {selectedApplication.account_number && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="text-sm text-gray-600 mb-1">Account Number</div>
                        <div className="font-medium text-gray-900">{selectedApplication.account_number}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Uploaded Documents */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <FileText size={16} />
                  Uploaded Documents
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Driver's License */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="text-sm font-medium text-gray-700 mb-2">Driver's License</div>
                    {selectedApplication.drivers_license_url ? (
                      <div className="space-y-2">
                        {selectedApplication.drivers_license_url.toLowerCase().endsWith('.pdf') ? (
                          <div className="flex items-center gap-2 text-gray-600">
                            <FileText size={40} className="text-red-500" />
                            <span className="text-sm">PDF Document</span>
                          </div>
                        ) : (
                          <img
                            src={selectedApplication.drivers_license_url}
                            alt="Driver's License"
                            className="w-full h-32 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90"
                            onClick={() => window.open(selectedApplication.drivers_license_url, '_blank')}
                          />
                        )}
                        <a
                          href={selectedApplication.drivers_license_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors"
                        >
                          <ExternalLink size={14} />
                          View Full Document
                        </a>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-400 italic">Not uploaded</div>
                    )}
                  </div>

                  {/* ID Document / Passport */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="text-sm font-medium text-gray-700 mb-2">
                      {selectedApplication.id_type === 'sa_id' ? 'SA ID Document' : 'Passport'}
                    </div>
                    {selectedApplication.id_document_url ? (
                      <div className="space-y-2">
                        {selectedApplication.id_document_url.toLowerCase().endsWith('.pdf') ? (
                          <div className="flex items-center gap-2 text-gray-600">
                            <FileText size={40} className="text-red-500" />
                            <span className="text-sm">PDF Document</span>
                          </div>
                        ) : (
                          <img
                            src={selectedApplication.id_document_url}
                            alt={selectedApplication.id_type === 'sa_id' ? 'SA ID' : 'Passport'}
                            className="w-full h-32 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90"
                            onClick={() => window.open(selectedApplication.id_document_url, '_blank')}
                          />
                        )}
                        <a
                          href={selectedApplication.id_document_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors"
                        >
                          <ExternalLink size={14} />
                          View Full Document
                        </a>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-400 italic">Not uploaded</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Rejection Reason (if rejected) */}
              {selectedApplication.status === 'rejected' && selectedApplication.rejection_reason && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <div className="text-sm font-medium text-red-800 mb-1">Rejection Reason:</div>
                  <div className="text-red-700">{selectedApplication.rejection_reason}</div>
                </div>
              )}

              {/* Approved Driver Actions */}
              {selectedApplication.status === 'approved' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-2 text-green-800 mb-3">
                    <CheckCircle size={18} />
                    <span className="font-medium">Driver Approved</span>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleSendPasswordReset(selectedApplication.email)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                    >
                      <Mail size={18} />
                      Send Password Reset Email
                    </button>
                    <a
                      href={`https://wa.me/${selectedApplication.phone.replace(/^0/, '27')}?text=${encodeURIComponent(
`Hi ${selectedApplication.full_name.split(' ')[0]},

This is Foodline. We've sent a password reset link to your email (${selectedApplication.email}).

Please check your inbox (and spam folder) to set your password and start using the Driver App.

📱 App: driver.onthemoov.co.za`
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-[#25D366] text-white rounded-lg font-medium hover:bg-[#20bd5a] transition-colors"
                    >
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      WhatsApp
                    </a>
                  </div>
                </div>
              )}

              {/* Action Buttons (only for pending) */}
              {selectedApplication.status === 'pending' && (
                <>
                  {/* Rejection Reason Input */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rejection Reason (required if rejecting)
                    </label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Enter reason for rejection..."
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={handleApprove}
                      disabled={processing}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      {processing ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      ) : (
                        <>
                          <Check size={20} />
                          Approve & Create Account
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleReject}
                      disabled={processing}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                    >
                      {processing ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      ) : (
                        <>
                          <X size={20} />
                          Reject Application
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverApplicationsAdmin;
